import { useTranslation } from 'react-i18next';
import { categories } from '@/mocks/categories';
import { Link } from 'react-router-dom';

const categoryFeatureCards = [
  {
    type: 'dark' as const,
    icon: 'ri-footprint-line',
    label: 'Popularne',
    title: 'Shoes',
    subtitle: 'Buty i sneakersy',
    catName: 'Shoes',
    url: 'https://kaspreadsheet.com/category-911-Shoes.html',
    imageUrl: 'https://readdy.ai/api/search-image?query=Luxury%20white%20leather%20sneakers%20with%20minimal%20design%20on%20warm%20sand%20colored%20studio%20backdrop%2C%20soft%20diffused%20lighting%20from%20large%20window%2C%20clean%20editorial%20product%20photography%2C%20elegant%20composition%20with%20subtle%20reflection&width=800&height=600&seq=cat-feat-shoes-v2&orientation=landscape',
  },
  {
    type: 'image' as const,
    label: 'Hoodies/Sweaters',
    title: 'Hoodies & Sweaters',
    subtitle: 'Bluzy i swetry',
    catName: 'Hoodies/Sweaters',
    url: 'https://kaspreadsheet.com/category-912-Hoodies%2FSweaters.html',
    imageUrl: 'https://readdy.ai/api/search-image?query=Warm%20camel%20colored%20oversized%20hoodie%20draped%20artistically%20on%20minimal%20wooden%20stool%2C%20soft%20golden%20hour%20light%20streaming%20through%20sheer%20curtain%2C%20editorial%20fashion%20photography%2C%20cozy%20aesthetic%20with%20organic%20fabric%20texture&width=800&height=600&seq=cat-feat-hoodies-v2&orientation=landscape',
  },
  {
    type: 'hybrid' as const,
    icon: 'ri-t-shirt-line',
    label: 'T-Shirts',
    title: 'T-Shirts',
    subtitle: 'Koszulki i t-shirty',
    catName: 'T-Shirts',
    url: 'https://kaspreadsheet.com/category-913-T-Shirts.html',
    imageUrl: 'https://readdy.ai/api/search-image?query=Premium%20white%20heavyweight%20cotton%20t-shirt%20on%20warm%20terracotta%20clay%20surface%2C%20soft%20morning%20sunlight%20casting%20gentle%20shadows%2C%20minimal%20editorial%20product%20photography%2C%20clean%20modern%20composition&width=800&height=500&seq=cat-feat-tshirt-v2&orientation=landscape',
  },
];

export default function CategoryHub() {
  const { t } = useTranslation('home');

  return (
    <section className="py-14 md:py-20 bg-background-100/60">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        {/* Section Header */}
        <div className="text-center mb-10 md:mb-14">
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 mb-4">
            {t('category_title')}
          </h2>
          <p className="text-foreground-500 text-sm md:text-base max-w-2xl mx-auto font-body">
            {t('category_subtitle')}
          </p>
        </div>

        {/* Feature Cards - 3 columns */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
          {/* Feature Card 1 - Dark themed Shoes */}
          <div className="relative group cursor-pointer rounded-2xl overflow-hidden bg-foreground-950 p-6 md:p-8 flex flex-col justify-between min-h-[260px] md:min-h-[300px] transition-transform duration-300 hover:scale-[1.02]">
            <div className="absolute top-0 right-0 w-32 h-32 md:w-40 md:h-40 opacity-10">
              <img src={categoryFeatureCards[0].imageUrl} alt="" className="w-full h-full object-cover object-top" loading="lazy" />
            </div>
            <div className="w-12 h-12 md:w-14 md:h-14 rounded-xl bg-foreground-800/40 flex items-center justify-center mb-auto">
              <i className="ri-footprint-line text-2xl text-background-50"></i>
            </div>
            <div>
              <p className="text-background-50/50 text-xs font-label uppercase tracking-wider mb-2">{categoryFeatureCards[0].label}</p>
              <h3 className="text-background-50 font-heading text-2xl md:text-3xl font-semibold mb-2">{categoryFeatureCards[0].title}</h3>
              <a
                href={categoryFeatureCards[0].url}
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 text-background-50/60 hover:text-background-50 text-sm font-label whitespace-nowrap transition-colors duration-200"
              >
                Przeglądaj <i className="ri-arrow-right-line text-base"></i>
              </a>
            </div>
          </div>

          {/* Feature Card 2 - Image Hoodies */}
          <div className="relative group cursor-pointer rounded-2xl overflow-hidden min-h-[260px] md:min-h-[300px] transition-transform duration-300 hover:scale-[1.02]">
            <img
              src={categoryFeatureCards[1].imageUrl}
              alt="Hoodies i bluzy Kakobuy"
              title="Kakobuy Spreadsheet Hoodies"
              className="absolute inset-0 w-full h-full object-cover object-top"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent"></div>
            <div className="absolute top-4 left-4">
              <span className="px-3 py-1.5 bg-background-50/20 backdrop-blur-sm rounded-full text-background-50 text-xs font-label whitespace-nowrap">
                <i className="ri-t-shirt-line mr-1.5"></i>{categoryFeatureCards[1].label}
              </span>
            </div>
            <div className="absolute bottom-6 left-6 right-6">
              <h3 className="text-background-50 font-heading text-xl md:text-2xl font-semibold mb-2">{categoryFeatureCards[1].title}</h3>
              <a
                href={categoryFeatureCards[1].url}
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 text-background-50/70 hover:text-background-50 text-sm font-label whitespace-nowrap transition-colors duration-200"
              >
                Przeglądaj <i className="ri-arrow-right-line text-base"></i>
              </a>
            </div>
          </div>

          {/* Feature Card 3 - Text + Image hybrid T-Shirts */}
          <div className="group cursor-pointer rounded-2xl overflow-hidden border border-background-200/60 bg-background-50 flex flex-col transition-transform duration-300 hover:scale-[1.02]">
            <div className="p-6 md:p-8 pb-4">
              <h3 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 mb-2">{categoryFeatureCards[2].title}</h3>
              <p className="text-foreground-500 text-sm mb-4 font-body">{categoryFeatureCards[2].subtitle}</p>
              <a
                href={categoryFeatureCards[2].url}
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 text-primary-500 hover:text-primary-600 text-sm font-label font-medium whitespace-nowrap transition-colors duration-200"
              >
                Przeglądaj <i className="ri-arrow-right-line text-base"></i>
              </a>
            </div>
            <div className="mt-auto h-40 md:h-48 overflow-hidden">
              <img
                src={categoryFeatureCards[2].imageUrl}
                alt="T-Shirts Kakobuy"
                title="Kakobuy Spreadsheet T-Shirts"
                className="w-full h-full object-cover object-top"
                loading="lazy"
              />
            </div>
          </div>
        </div>

        {/* All Categories Grid with Images */}
        <div className="mt-10 md:mt-14">
          <h3 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 mb-6">
            Wszystkie kategorie
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
            {categories.slice(3).map((cat) => (
              <a
                key={cat.seq}
                href={cat.url}
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center gap-3 px-3 py-3 bg-background-50 rounded-xl border border-background-200/60 hover:border-primary-300/50 hover:bg-primary-50/30 transition-all duration-200 group cursor-pointer"
              >
                <div className="w-11 h-11 rounded-lg overflow-hidden flex-shrink-0">
                  <img
                    src={cat.imageUrl}
                    alt={cat.name}
                    loading="lazy"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <span className="text-sm font-label text-foreground-700 group-hover:text-foreground-950 whitespace-nowrap transition-colors duration-200">
                  {cat.name}
                </span>
              </a>
            ))}
          </div>
        </div>

        {/* View All Link */}
        <div className="text-center mt-10">
          <Link
            to="/czym-jest-kakobuy-spreadsheet"
            className="inline-flex items-center gap-2 text-foreground-500 hover:text-foreground-950 text-sm font-label whitespace-nowrap transition-colors duration-200 group"
          >
            {t('btn_view_all')} artykuły
            <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform duration-200"></i>
          </Link>
        </div>
      </div>
    </section>
  );
}